import { type Dispatch } from '@reduxjs/toolkit';
import { showSnackbar } from './reducers/snackbarSlice';

export interface ServerError {
  error: {
    status: number;
    data: {
      message: string;
      errors: unknown;
    };
  };
}

interface ValidationErrorItem {
  path: string;
  message: string;
}

export const handleApiResult = (successMessage?: string, { handle401Error } = { handle401Error: false }) =>
  async (_: unknown,
    { dispatch, queryFulfilled }: { dispatch: Dispatch; queryFulfilled: Promise<{ data: unknown }> }) => {
    try {
      await queryFulfilled;
      if (successMessage != null) {
        dispatch(showSnackbar({ severity: 'success', message: successMessage }));
      }
    } catch (error) {
      if (isServerError(error) && (handle401Error || error.error.status != 401)) {
        let message = error.error.data.message;

        if (isDataHasMultiError(error.error.data.errors)) {
          const errorMessages = error.error.data.errors
            .map(error => `${error.path}: ${error.message}`)
            .join('\n');

          message = message.concat('\n', errorMessages);
        }

        dispatch(showSnackbar({ severity: 'error', message }));
      } else {
        console.error('api.error:', error);
      }
    }
  };

const isObjectWithKey = <T>(obj: unknown, key: string): obj is Record<string, T> => {
  return typeof obj === 'object' && obj !== null && key in obj;
};

export const isServerError = (error: unknown): error is ServerError => {
  return (
    isObjectWithKey(error, 'error') &&
    isObjectWithKey(error.error, 'data') &&
    isObjectWithKey(error.error.data, 'message') &&
    typeof error.error.status === 'number' &&
    typeof error.error.data.message === 'string'
  );
};

const isValidationErrorItem = (error: unknown): error is ValidationErrorItem => {
  return (
    isObjectWithKey<string>(error, 'path') &&
    isObjectWithKey<string>(error, 'message')
  );
};

const isDataHasMultiError = (errors: unknown): errors is ValidationErrorItem[] => {
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    errors.every(isValidationErrorItem)
  );
};

/**
 * Why the server refused a request, for the message a dialog or card shows beside its form, or
 * undefined when it gave no reason (a network failure), so the caller says what could not be done.
 *
 * A mutation's `unwrap()`, which every dialog and card awaits, throws the error itself:
 * `{ status, data }`. `isServerError` matches what `queryFulfilled` rejects with in
 * `onQueryStarted`, which wraps it one level deeper as `{ error: { status, data } }`. Reading the
 * thrown error with `isServerError` therefore never matched, and every form showed its fallback
 * while the reason only went by in a snackbar, or nowhere where the endpoint had none (the licence
 * file import, the certificate). Both shapes are read here. A validation failure gives each field's
 * own message rather than "Validation failed".
 */
export const serverMessage = (caught: unknown): string | undefined => {
  const data: unknown = isServerError(caught)
    ? caught.error.data
    : isObjectWithKey(caught, 'data') ? caught.data : undefined;

  if (!isObjectWithKey(data, 'message') || typeof data.message !== 'string') return undefined;

  return isDataHasMultiError(data.errors) ? data.errors.map(item => item.message).join(' ') : data.message;
};
